/** @format */

import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useMatches } from "@tanstack/react-router";
import type React from "react";
import { Suspense } from "react";
import { z } from "zod";

import { runPrefetchUtils, runQueryOptions } from "@/api/runsApi";
import { PlaybackProvider } from "@/context/playbackContext";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import PageLoader from "@/utils/PageLoader";
import { RouteErrorComponent } from "@/utils/RouteErrorComponent";

// Lazy import OUTSIDE component to avoid re-creating on every render
const RunPage = lazyWithRetry(() => import("@/pages/Run/Run"));

// Layout component that renders RunPage or child route via Outlet
const RunLayout: React.FC = () => {
  const matches = useMatches();
  // Check if there's a child route (more than just /run/$runID)
  const hasChildRoute = matches.some(
    match => match.routeId !== "/run/$runID" && match.routeId.startsWith("/run/$runID/")
  );

  if (hasChildRoute) {
    // Render child route (e.g., widget wrapper)
    return <Outlet />;
  }

  // Render the main Run page
  return (
    <Suspense fallback={<PageLoader />}>
      <RunPage />
    </Suspense>
  );
};

// Type-safe search params with Zod
const runSearchSchema = z
  .object({
    detailVehicleID: z.string().optional(),
    lap: z.coerce.number().optional(),
    playbackLap: z.coerce.number().optional(),
    playbackTimestamp: z.coerce.number().optional(),
    lapStart: z.coerce.number().optional(),
    lapEnd: z.coerce.number().optional(),
    lapProgress: z.coerce.number().optional(),
    activeView: z.string().optional(),
    // Shareable filter params
    selectedLapsFilter: z.string().optional(),
    selectedVehiclesFilter: z.string().optional(),
    activeStages: z.string().optional(), // Comma-separated string
    filterInPit: z.coerce.boolean().optional(),
    filterInLap: z.coerce.boolean().optional(),
    filterOutLap: z.coerce.boolean().optional(),
    filterCaution: z.coerce.boolean().optional(),
    filterRestarts: z.coerce.boolean().optional(),
    filterImputed: z.coerce.boolean().optional(),
    filterOutOfIqr: z.coerce.boolean().optional(),
    filterLapDown: z.coerce.boolean().optional(),
    showLegend: z.coerce.boolean().optional(),
    showProjections: z.coerce.boolean().optional(),
    selectedProjectionModel: z.string().optional(),
  })
  // `passthrough()` lets per-widget keys (e.g. `pl-custom-visualization` for
  // each Perspective widget instance, keyed off its slugified comp name)
  // ride through without explicit schema entries — they're added at write
  // time by the widgets themselves.
  .passthrough();

export const Route = createFileRoute("/run/$runID")({
  validateSearch: runSearchSchema,

  // Handle "live" route redirect (replaces useLiveParams)
  beforeLoad: async ({
    params,
    context,
    search,
  }: {
    params: { runID: string };
    context: { queryClient: QueryClient };
    search: z.infer<typeof runSearchSchema>;
  }) => {
    if (params.runID === "live") {
      // Fetch live runs to get the actual run ID
      // Uses runQueryOptions factory for single source of truth
      const liveRunsData = await context.queryClient.fetchQuery(runQueryOptions.live());

      const liveRuns = liveRunsData?.data || [];

      if (!liveRuns || liveRuns.length === 0) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: "/" });
      }

      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: "/run/$runID",
        params: { runID: liveRuns[0].id },
        search, // Preserve search params
      });
    }
  },

  // Prefetch core run data (detail, vehicles, vehicle_laps) during navigation.
  // Fire-and-forget — route renders immediately, data fills in.
  loader: async ({
    params,
    context,
  }: {
    params: { runID: string };
    context: { queryClient: QueryClient };
  }) => {
    void runPrefetchUtils.core(context.queryClient, params.runID);
  },

  component: () => (
    <PlaybackProvider>
      <RunLayout />
    </PlaybackProvider>
  ),
  errorComponent: RouteErrorComponent,
  pendingComponent: () => <PageLoader />,
});
